import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { dashboardApi, workOrdersApi } from '../services/api'
import { DashboardKPI, StoricoProduzioneItem, PerformanceTecnico } from '../types'
import { Printer, TrendingUp } from 'lucide-react'
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell, Legend } from 'recharts'

const MESI = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre']
const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16', '#ec4899', '#14b8a6']

function LightTooltip({ active, payload, label }: { active?: boolean; payload?: { name: string; value: number; color: string }[]; label?: string }) {
  if (!active || !payload?.length) return null
  return (
    <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '6px 10px', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', fontSize: 11 }}>
      <p style={{ color: '#6b7280', fontWeight: 600, marginBottom: 4 }}>{label}</p>
      {payload.map(p => (
        <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
          <span style={{ color: '#111827', fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}>
            € {p.value.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
          </span>
          <span style={{ color: '#9ca3af', fontSize: 10 }}>{p.name}</span>
        </div>
      ))}
    </div>
  )
}

function fmtEur(v: number | null | undefined): string {
  return `€ ${(v ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 0 })}`
}

interface StatBoxProps { label: string; value: string; accent: string; sub?: string }
function StatBox({ label, value, accent, sub }: StatBoxProps) {
  return (
    <div style={{
      flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderTop: `3px solid ${accent}`,
      borderRadius: 6, padding: '8px 10px', minWidth: 0,
    }}>
      <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94a3b8' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.25, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 8, color: '#94a3b8', marginTop: 1 }}>{sub}</div>}
    </div>
  )
}

const thS: React.CSSProperties = { textAlign: 'left', padding: '3px 6px', fontSize: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', borderBottom: '1.5px solid #cbd5e1', whiteSpace: 'nowrap' }
const tdS: React.CSSProperties = { padding: '3px 6px', fontSize: 9.5, color: '#1e293b', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }

export default function DashboardStampaA3() {
  const { data: kpi, isLoading: kpiLoading } = useQuery<DashboardKPI>({
    queryKey: ['dashboard-kpi'],
    queryFn: () => dashboardApi.kpi().then(r => r.data),
  })
  const { data: storicoProd, isLoading: storicoLoading } = useQuery<StoricoProduzioneItem[]>({
    queryKey: ['storico-produzione'],
    queryFn: () => dashboardApi.storicoProduzione().then(r => r.data),
  })
  const nowMonth = new Date().getMonth() + 1
  const { data: performanceTecnici, isLoading: perfLoading } = useQuery<PerformanceTecnico[]>({
    queryKey: ['performance-tecnici', nowMonth],
    queryFn: () => dashboardApi.perTecnico({ mese: nowMonth }).then(r => r.data),
  })
  const { data: perRegione, isLoading: regioneLoading } = useQuery<{ regione: string; assegnato: number; ricevuto: number; totale: number }[]>({
    queryKey: ['per-regione'],
    queryFn: () => dashboardApi.perRegione().then(r => r.data),
  })
  const { data: andamento, isLoading: andamentoLoading } = useQuery({
    queryKey: ['andamento-mensile'],
    queryFn: () => dashboardApi.andamentoMensile().then(r => r.data),
  })
  const { data: perStato, isLoading: statoLoading } = useQuery({
    queryKey: ['per-stato'],
    queryFn: () => dashboardApi.perStato().then(r => r.data),
  })
  const { data: eseguitiMappa, isLoading: mappaLoading } = useQuery({
    queryKey: ['stampa-a3-eseguiti-mappa'],
    queryFn: () => workOrdersApi.list({ stato: 'eseguito', per_page: 5000 }).then(r => r.data.items as any[]),
  })

  const loading = kpiLoading || storicoLoading || perfLoading || regioneLoading || andamentoLoading || statoLoading || mappaLoading

  const storicoLavorati = (storicoProd ?? [])
    .filter(r => r.completati > 0 || r.produzione_eur > 0)
    .slice()
    .reverse()
  const performanceConWo = (performanceTecnici ?? []).filter(t => t.wo_completati > 0)
  const oggi = new Date()
  const chartData = (andamento?.dati ?? []).map((d: { mese: number; fatturato: number }) => ({
    mese: MESI[d.mese - 1].slice(0, 3), fatturato: d.fatturato,
  }))
  const statoData = (perStato ?? []).map((d: { stato: string; count: number }) => ({ name: d.stato.replace(/_/g, ' '), value: d.count }))

  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current || mappaLoading) return
    const map = L.map(mapRef.current, { center: [41.9, 12.5], zoom: 6, zoomControl: false, attributionControl: false })
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map)
    mapInstanceRef.current = map
    const bounds: [number, number][] = []
    for (const wo of eseguitiMappa ?? []) {
      const lat = Number(wo.cliente_lat)
      const lng = Number(wo.cliente_lng)
      if (!lat || !lng) continue
      bounds.push([lat, lng])
      L.circleMarker([lat, lng], { radius: 4, color: '#fff', weight: 1, fillColor: '#22c55e', fillOpacity: 0.85 }).addTo(map)
    }
    setTimeout(() => {
      map.invalidateSize()
      if (bounds.length > 0) map.fitBounds(L.latLngBounds(bounds), { padding: [20, 20] })
    }, 150)
    return () => { map.remove(); mapInstanceRef.current = null }
  }, [mappaLoading, eseguitiMappa])

  return (
    <div style={{ background: '#e2e8f0', minHeight: '100vh' }}>
      <style>{`
        @page { size: A3 landscape; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          body, html { background: #fff !important; }
          .a3-sheet { box-shadow: none !important; margin: 0 !important; width: auto !important; min-height: 0 !important; }
        }
      `}</style>

      <div className="no-print" style={{ padding: 16, display: 'flex', justifyContent: 'center', gap: 10 }}>
        <button
          onClick={() => window.print()}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10,
            background: loading ? '#93c5fd' : '#1E40AF', color: '#fff', border: 'none', fontSize: 14, fontWeight: 700,
            cursor: loading ? 'default' : 'pointer', boxShadow: '0 4px 12px rgba(30,64,175,0.25)',
          }}
        >
          <Printer size={16} /> {loading ? 'Caricamento dati…' : 'Stampa A3 orizzontale'}
        </button>
      </div>

      {/* Foglio A3 orizzontale: 420 x 297mm */}
      <div className="a3-sheet" style={{
        width: '400mm', minHeight: '277mm', margin: '0 auto 24px', background: '#fff',
        boxShadow: '0 8px 30px rgba(0,0,0,0.15)', padding: '10mm 12mm',
        display: 'flex', flexDirection: 'column', gap: '4mm',
        fontFamily: "'Figtree', -apple-system, sans-serif",
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', borderBottom: '2.5px solid #1E40AF', paddingBottom: '2.5mm' }}>
          <div>
            <div style={{ fontSize: 19, fontWeight: 800, color: '#1E40AF', letterSpacing: '-0.01em' }}>Delta Poste — Report Produzione</div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>
              {MESI[oggi.getMonth()]} {oggi.getFullYear()} — generato il {oggi.toLocaleDateString('it-IT')} alle {oggi.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8' }}>XSophia · X4MeS</div>
        </div>

        {/* Riga KPI principali */}
        <div style={{ display: 'flex', gap: '3mm' }}>
          <StatBox label="€ Produzione Mese" value={fmtEur(kpi?.produzione_mese)} accent="#8b5cf6" sub="valore WO eseguiti nel mese" />
          <StatBox label="Totale Prodotto" value={fmtEur(kpi?.dal_inizio?.produzione_totale)} accent="#8b5cf6" sub={`dal ${kpi?.dal_inizio?.data_inizio ? new Date(kpi.dal_inizio.data_inizio).toLocaleDateString('it-IT') : '—'}`} />
          <StatBox label="Giorni Lavorati" value={String(kpi?.dal_inizio?.giorni_lavorati ?? 0)} accent="#dc2626" sub="dall'inizio attività" />
          <StatBox label="Giorni con Chiusure" value={String(kpi?.produttivita?.giorni_attivi ?? 0)} accent="#dc2626" sub="nel mese" />
          <StatBox label="Tecnici Operativi" value={String(kpi?.produttivita?.tecnici_operativi ?? 0)} accent="#1E40AF" sub={kpi?.produttivita?.wo_per_tecnico ? `${kpi.produttivita.wo_per_tecnico} WO/tecnico` : undefined} />
          <StatBox label="Completati / Mese" value={String(kpi?.work_orders?.completati_questo_mese ?? 0)} accent="#10b981" />
        </div>

        {/* Grafico fatturato mensile + Media €/Giorno·Risorsa con storico */}
        <div style={{ display: 'flex', gap: '4mm' }}>
          <div style={{ flex: 2, border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px 2px' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>Fatturato Mensile {oggi.getFullYear()}</div>
            <ResponsiveContainer width="100%" height={120}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="fGradPrint" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#F97316" stopOpacity={0.18} />
                    <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                <XAxis dataKey="mese" tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={40}
                  tickFormatter={v => `€${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<LightTooltip />} cursor={{ stroke: 'rgba(249,115,22,0.15)', strokeWidth: 1 }} />
                <Area type="monotone" dataKey="fatturato" name="Fatturato" stroke="#F97316" strokeWidth={2}
                  fill="url(#fGradPrint)" dot={{ fill: '#F97316', strokeWidth: 0, r: 2.5 }}
                  activeDot={{ r: 4, fill: '#F97316', strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderTop: '3px solid #F97316', borderRadius: 6, padding: '6px 10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <TrendingUp size={12} color="#F97316" />
              <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#94a3b8' }}>Media €/Giorno·Risorsa</div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.1 }}>
              {fmtEur(kpi?.produttivita?.media_eur_giorno_risorsa)}
            </div>
            <div style={{ fontSize: 8, color: '#94a3b8', marginBottom: 4 }}>mese corrente</div>
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 2 }}>
              {storicoLavorati.map(m => (
                <div key={`${m.anno}-${m.mese}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 9, padding: '1px 0' }}>
                  <span style={{ color: '#64748b' }}>{MESI[m.mese - 1].slice(0, 3)} {m.anno}</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, color: '#0f172a' }}>{fmtEur(m.media_eur_giorno_risorsa)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Corpo: 3 colonne */}
        <div style={{ display: 'flex', gap: '4mm', flex: 1, minHeight: 0 }}>

          {/* Storico produzione */}
          <div style={{ flex: 1.15, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#f8fafc', padding: '4px 8px', fontSize: 10, fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
              Storico Produzione — Mesi Lavorati
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Mese</th>
                  <th style={{ ...thS, textAlign: 'right' }}>€ Prod.</th>
                  <th style={{ ...thS, textAlign: 'right' }}>WO</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Tecn.</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Gg Op.</th>
                  <th style={{ ...thS, textAlign: 'right' }}>€/Gg·Ris.</th>
                </tr>
              </thead>
              <tbody>
                {storicoLavorati.length === 0 ? (
                  <tr><td colSpan={6} style={{ ...tdS, textAlign: 'center', color: '#94a3b8' }}>Nessun dato</td></tr>
                ) : storicoLavorati.map((r, i) => (
                  <tr key={`${r.anno}-${r.mese}`} style={{ background: i === 0 ? '#eff6ff' : undefined }}>
                    <td style={{ ...tdS, fontWeight: i === 0 ? 700 : 500 }}>{MESI[r.mese - 1].slice(0, 3)} {r.anno}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{fmtEur(r.produzione_eur)}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{r.completati}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{r.n_tecnici}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{r.giorni_operativi}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{fmtEur(r.media_eur_giorno_risorsa)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Performance tecnici */}
          <div style={{ flex: 1.15, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#f8fafc', padding: '4px 8px', fontSize: 10, fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
              Performance Tecnici — {MESI[oggi.getMonth()]} {oggi.getFullYear()}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Tecnico</th>
                  <th style={{ ...thS, textAlign: 'right' }}>WO</th>
                  <th style={{ ...thS, textAlign: 'right' }}>% Compl.</th>
                  <th style={{ ...thS, textAlign: 'right' }}>€ Prod.</th>
                </tr>
              </thead>
              <tbody>
                {performanceConWo.length === 0 ? (
                  <tr><td colSpan={4} style={{ ...tdS, textAlign: 'center', color: '#94a3b8' }}>Nessun dato</td></tr>
                ) : performanceConWo.map(t => (
                  <tr key={t.tecnico_id}>
                    <td style={{ ...tdS, fontWeight: 500 }}>{t.nome} {t.cognome}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{t.wo_completati}/{t.wo_assegnati}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{t.tasso_completamento}%</td>
                    <td style={{ ...tdS, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{t.produzione_eur != null ? fmtEur(t.produzione_eur) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* WO per regione */}
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#f8fafc', padding: '4px 8px', fontSize: 10, fontWeight: 700, color: '#1e293b', borderBottom: '1px solid #e2e8f0' }}>
              WO Aperti per Regione
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thS}>Regione</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Assegn.</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Ricev.</th>
                  <th style={{ ...thS, textAlign: 'right' }}>Tot.</th>
                </tr>
              </thead>
              <tbody>
                {(perRegione ?? []).length === 0 ? (
                  <tr><td colSpan={4} style={{ ...tdS, textAlign: 'center', color: '#94a3b8' }}>Nessun dato</td></tr>
                ) : (perRegione ?? []).map(r => (
                  <tr key={r.regione}>
                    <td style={{ ...tdS, fontWeight: 500 }}>{r.regione}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{r.assegnato}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{r.ricevuto}</td>
                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 700 }}>{r.totale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Distribuzione per stato + mappa interventi eseguiti */}
        <div style={{ display: 'flex', gap: '4mm', height: '95mm', minHeight: '95mm', maxHeight: '95mm' }}>
          <div style={{ flex: 1, minHeight: 0, border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>Distribuzione per Stato</div>
            <div style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={statoData} dataKey="value" nameKey="name" cx="50%" cy="48%" outerRadius={48} innerRadius={26} paddingAngle={3} strokeWidth={0}>
                    {statoData.map((_: unknown, i: number) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 9 }} iconSize={8} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div style={{ flex: 1.6, minHeight: 0, border: '1px solid #e2e8f0', borderRadius: 6, padding: '4px 8px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: '#1e293b', marginBottom: 2 }}>
              Mappa Interventi Eseguiti ({(eseguitiMappa ?? []).length})
            </div>
            <div ref={mapRef} style={{ flex: 1, minHeight: 0, borderRadius: 4, overflow: 'hidden' }} />
          </div>
        </div>

        <div style={{ textAlign: 'center', fontSize: 8, color: '#94a3b8', borderTop: '1px solid #f1f5f9', paddingTop: '2mm' }}>
          Report generato automaticamente da XSophia — Delta ICT
        </div>
      </div>
    </div>
  )
}
